import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

/**
 * Cross-platform storage utility that uses:
 * - Capacitor Preferences on iOS/Android (persistent, reliable)
 * - localStorage on web (fallback)
 */

const isNativePlatform = Capacitor.isNativePlatform();

export const storage = {
  async getItem(key: string): Promise<string | null> {
    if (isNativePlatform) {
      const { value } = await Preferences.get({ key });
      return value;
    }
    return localStorage.getItem(key);
  },

  async setItem(key: string, value: string): Promise<void> {
    if (isNativePlatform) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  },

  async removeItem(key: string): Promise<void> {
    if (isNativePlatform) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  },

  async clear(): Promise<void> {
    if (isNativePlatform) {
      await Preferences.clear();
    } else {
      localStorage.clear();
    }
  }
};

// Synchronous versions for backward compatibility (only work on web)
// These should be avoided on native platforms
export const storageSync = {
  getItem(key: string): string | null {
    if (isNativePlatform) {
      console.warn('storageSync.getItem called on native platform - use async storage.getItem instead');
      return null;
    }
    return localStorage.getItem(key);
  },

  setItem(key: string, value: string): void {
    if (isNativePlatform) {
      console.warn('storageSync.setItem called on native platform - use async storage.setItem instead');
      // Still try to set it for immediate availability
      Preferences.set({ key, value }).catch(console.error);
      return;
    }
    localStorage.setItem(key, value);
  },

  removeItem(key: string): void {
    if (isNativePlatform) {
      console.warn('storageSync.removeItem called on native platform - use async storage.removeItem instead');
      Preferences.remove({ key }).catch(console.error);
      return;
    }
    localStorage.removeItem(key);
  }
};

export default storage;
